import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { trackFormSubmit } from "@/utils/tracking";
import { storeUTMParams, useUTMParams } from "@/hooks/useUTMParams";
import { logAudit } from "@/utils/auditLog";

interface VehicleInquiryFormProps {
  vehicleId: string;
  vehicleName: string;
}

export default function VehicleInquiryForm({ vehicleId, vehicleName }: VehicleInquiryFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [bestTime, setBestTime] = useState("anytime");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const utm = useUTMParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }

    setIsSubmitting(true);
    storeUTMParams(utm);

    try {
      // 1. Insert into contact_submissions via RPC
      // SECURITY: Don't fabricate email addresses — use actual email or empty string
      const { error } = await supabase.rpc("submit_contact_form", {
        p_name: name.trim(),
        p_email: email.trim() || `noemail+${Date.now()}@placeholder.local`,
        p_phone: phone.trim(),
        p_vehicle_id: vehicleId,
        p_message: `Vehicle Inquiry: ${vehicleName}\nBest time to contact: ${bestTime}${message ? `\n\n${message}` : ''}`,
      });

      if (error) throw error;

      // Log lead capture to audit log
      await logAudit({
        entityType: 'lead',
        action: 'captured',
        metadata: {
          lead_name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          vehicle_id: vehicleId,
          vehicle_name: vehicleName,
          source: 'vehicle_inquiry',
        },
      });

      // CRM records will be created manually by admins in the Leads Management dashboard
      // to avoid client-side RLS restrictions for anonymous users.

      // 3. Send email notification
      supabase.functions.invoke("send-email", {
        body: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          message: `Vehicle Inquiry for ${vehicleName}\nBest time: ${bestTime}${message ? `\n${message}` : ''}`,
        },
      }).catch(console.error);

      // 4. Track conversion
      trackFormSubmit("vehicle_inquiry", vehicleId);

      setIsSubmitted(true);
      toast.success("Inquiry sent! We'll contact you shortly.");
    } catch (err) {
      console.error("Failed to submit inquiry:", err);
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-card border rounded-xl p-6 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="font-bold text-lg mb-1">Inquiry Sent!</h3>
        <p className="text-sm text-muted-foreground">
          We'll get back to you about the {vehicleName} shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border rounded-xl p-5">
      <h3 className="font-bold text-lg mb-1">Interested in this vehicle?</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Send us an inquiry and we'll get back to you ASAP.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Label htmlFor="inquiry-name" className="text-sm">Name *</Label>
          <Input
            id="inquiry-name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Your full name"
            required
          />
        </div>
        <div>
          <Label htmlFor="inquiry-phone" className="text-sm">Phone *</Label>
          <Input
            id="inquiry-phone"
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            required
          />
        </div>
        <div>
          <Label htmlFor="inquiry-email" className="text-sm">Email</Label>
          <Input
            id="inquiry-email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
        </div>
        <div>
          <Label htmlFor="inquiry-time" className="text-sm">Best time to contact</Label>
          <Select value={bestTime} onValueChange={setBestTime}>
            <SelectTrigger id="inquiry-time"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anytime">Anytime</SelectItem>
              <SelectItem value="morning">Morning (9AM - 12PM)</SelectItem>
              <SelectItem value="afternoon">Afternoon (12PM - 5PM)</SelectItem>
              <SelectItem value="evening">Evening (5PM - 8PM)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="inquiry-message" className="text-sm">Message (optional)</Label>
          <Textarea
            id="inquiry-message"
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Any questions about this vehicle?"
            rows={2}
          />
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          <Send className="h-4 w-4 mr-2" />
          {isSubmitting ? "Sending..." : "Send Inquiry"}
        </Button>
      </form>
    </div>
  );
}
